import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../services/api';
import V2Header from '../../components/v2/layout/V2Header';
import V2Container from '../../components/v2/layout/V2Container';
import V2Badge from '../../components/v2/ui/V2Badge';
import V2Button from '../../components/v2/forms/V2Button';
import V2Input from '../../components/v2/forms/V2Input';
import V2Select from '../../components/v2/forms/V2Select';
import V2Textarea from '../../components/v2/forms/V2Textarea';
import EmptyState from '../../components/v2/ui/EmptyState';
import '../../v2-theme.css';
import './RestaurantDashboard.css';

/**
 * Restaurant Dashboard Page
 * Matches restaurant-dashboard.html exactly
 * Features: Post shifts, view active shifts, manage workers
 */
const RestaurantDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { socket } = useSocket();

  const [restaurantProfile, setRestaurantProfile] = useState(null);
  const [activeShifts, setActiveShifts] = useState([]);
  const [formData, setFormData] = useState({
    position: '',
    date: '',
    startTime: '',
    endTime: '',
    hourlyPay: '20.00',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const POSITIONS = [
    { value: 'dishwasher', label: 'Dishwasher' },
    { value: 'prep-cook', label: 'Prep Cook' },
    { value: 'line-cook', label: 'Line Cook' },
    { value: 'sous-chef', label: 'Sous Chef' },
    { value: 'head-chef', label: 'Head Chef' },
    { value: 'event-cook', label: 'Event Cook' },
    { value: 'banquet', label: 'Banquet' },
    { value: 'bartender', label: 'Bartender' },
    { value: 'barback', label: 'Barback' }
  ];

  // Fetch restaurant profile
  const fetchRestaurantProfile = async () => {
    try {
      const response = await api.get(`/profiles/restaurant/${user.id}`);
      setRestaurantProfile(response.data);
    } catch (err) {
      console.error('Error fetching restaurant profile:', err);
    }
  };

  // Fetch active shifts for this restaurant
  const fetchActiveShifts = async () => {
    try {
      // GET /jobs with status filter automatically returns restaurant's jobs
      const response = await api.get('/jobs', {
        params: {
          status: 'all' // Get all statuses (open, pending, claimed, etc.)
        }
      });

      // Transform jobs to include flattened worker info from applications
      const jobs = (response.data.jobs || []).map(job => {
        const acceptedApp = job.applications?.find(app => app.status === 'accepted');
        return {
          ...job,
          worker_id: acceptedApp?.worker_profiles?.user_id || null,
          worker_name: acceptedApp?.worker_profiles
            ? `${acceptedApp.worker_profiles.first_name} ${acceptedApp.worker_profiles.last_name}`
            : null
        };
      });

      setActiveShifts(jobs);
    } catch (err) {
      console.error('Error fetching active shifts:', err);
    }
  };

  // Handle form input changes
  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Post new shift
  const handlePostShift = async (e) => {
    e.preventDefault();
    setError('');

    // Validation: End time must be after start time
    if (formData.endTime <= formData.startTime) {
      setError('End time must be after start time.');
      return;
    }

    // Validation: Hourly pay minimum $20
    if (parseFloat(formData.hourlyPay) < 20) {
      setError('Hourly pay must be at least $20.00/hr.');
      return;
    }

    // Check subscription tier before posting
    const isUnlimited = restaurantProfile?.subscription_tier === 'unlimited' || restaurantProfile?.admin_grant_unlimited;

    if (!isUnlimited) {
      // Redirect to payment checkout for $19 shift posting fee
      const shiftData = {
        position: formData.position,
        shift_date: formData.date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        hourly_rate: parseFloat(formData.hourlyPay), // Backend expects hourly_rate
        notes: formData.notes
      };

      navigate('/checkout/shift-payment', {
        state: { shiftData }
      });
      return;
    }

    // Unlimited subscription - post directly
    setLoading(true);

    try {
      await api.post('/jobs', {
        position: formData.position,
        shift_date: formData.date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        hourly_rate: parseFloat(formData.hourlyPay),
        notes: formData.notes
      });

      // Reset form
      setFormData({
        position: '',
        date: '',
        startTime: '',
        endTime: '',
        hourlyPay: '20.00',
        notes: ''
      });

      // Refresh active shifts
      await fetchActiveShifts();

      alert('Shift posted successfully!');
    } catch (err) {
      console.error('Error posting shift:', err);
      setError(err.response?.data?.error || 'Failed to post shift. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete/cancel shift
  const handleDeleteShift = async (shiftId, isClaimed) => {
    const confirmMessage = isClaimed
      ? 'Are you sure you want to cancel this shift? The worker will be notified.'
      : 'Are you sure you want to delete this shift?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await api.delete(`/jobs/${shiftId}`);
      await fetchActiveShifts();
      alert(isClaimed ? 'Shift cancelled successfully.' : 'Shift deleted successfully.');
    } catch (err) {
      console.error('Error deleting shift:', err);
      alert(err.response?.data?.error || 'Failed to delete shift. Please try again.');
    }
  };

  // View worker profile
  const handleViewWorkerProfile = (workerId) => {
    navigate(`/worker-profile/${workerId}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Format time for display
  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get position label
  const getPositionLabel = (value) => {
    const position = POSITIONS.find(p => p.value === value);
    return position ? position.label : value;
  };

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchRestaurantProfile();
      fetchActiveShifts();
    }
  }, [user]);

  // Socket.IO listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    // Listen for shift claims
    socket.on('shift_claimed', ({ jobId, workerId }) => {
      fetchActiveShifts(); // Refresh to show claimed status
    });

    // Listen for shift cancellations by workers
    socket.on('shift_cancelled', (jobId) => {
      fetchActiveShifts(); // Refresh to show unclaimed status
    });

    return () => {
      socket.off('shift_claimed');
      socket.off('shift_cancelled');
    };
  }, [socket]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await signOut();
      navigate('/');
    }
  };

  // Set minimum date to today
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="restaurant-dashboard-page">
      <V2Header
        title="Hands On The Fly"
        businessName={restaurantProfile?.business_name || 'Restaurant'}
        userType="restaurant"
        profilePath="/profile/restaurant"
        onLogout={handleLogout}
      />

      <V2Container maxWidth="lg">
        <div className="dashboard-header">
          <h2 className="v2-heading-xl">Restaurant Dashboard</h2>
          <p className="v2-text-silver">Post shifts, manage staff needs</p>
        </div>

        <div className="restaurant-dashboard-grid">
          {/* Post New Shift */}
          <div className="dashboard-section">
            <h3 className="v2-heading-lg">Post New Shift</h3>
            <form className="post-shift-form" onSubmit={handlePostShift}>
              {error && <div className="v2-error-message">{error}</div>}

              <V2Select
                label="Position"
                name="position"
                value={formData.position}
                onChange={(e) => handleChange('position', e.target.value)}
                options={[{ value: '', label: 'Select Position' }, ...POSITIONS]}
                required
                disabled={loading}
              />

              <div className="form-row">
                <V2Input
                  label="Date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  min={today}
                  required
                  disabled={loading}
                />
                <V2Input
                  label="Start Time"
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-row">
                <V2Input
                  label="End Time"
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                  required
                  disabled={loading}
                />
                <V2Input
                  label="Hourly Pay (minimum $20/hr)"
                  type="number"
                  name="hourlyPay"
                  value={formData.hourlyPay}
                  onChange={(e) => handleChange('hourlyPay', e.target.value)}
                  min="20"
                  step="0.50"
                  required
                  disabled={loading}
                />
              </div>

              <V2Textarea
                label="Additional Notes (Optional)"
                name="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Experience requirements, dress code, etc."
                disabled={loading}
              />

              <V2Button
                type="submit"
                variant="steel"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Posting...' : 'Post Shift'}
              </V2Button>
            </form>
          </div>

          {/* Active Shifts */}
          <div className="dashboard-section">
            <h3 className="v2-heading-lg">Active Shifts</h3>
            <div className="shifts-list">
              {activeShifts.length === 0 ? (
                <EmptyState message="No active shifts" />
              ) : (
                activeShifts.map(shift => (
                  <div key={shift.id} className="shift-card">
                    <div className="shift-header">
                      <div className="shift-position">{getPositionLabel(shift.position)}</div>
                      <V2Badge variant={shift.status === 'filled' ? 'success' : 'secondary'}>
                        {shift.status === 'filled' ? 'Claimed' : 'Open'}
                      </V2Badge>
                    </div>
                    <div className="shift-details">
                      <div><strong>Date:</strong> {formatDate(shift.shift_date)}</div>
                      <div>
                        <strong>Time:</strong> {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </div>
                      <div><strong>Pay:</strong> ${parseFloat(shift.hourly_rate).toFixed(2)}/hr</div>
                      {shift.worker_name && (
                        <div><strong>Worker:</strong> {shift.worker_name}</div>
                      )}
                    </div>
                    <div className="shift-actions">
                      {shift.status === 'filled' && shift.worker_id && (
                        <button
                          className="shift-btn"
                          onClick={() => handleViewWorkerProfile(shift.worker_id)}
                        >
                          View Profile
                        </button>
                      )}
                      <button
                        className="shift-btn delete"
                        onClick={() => handleDeleteShift(shift.id, shift.status === 'filled')}
                      >
                        {shift.status === 'filled' ? 'Cancel' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </V2Container>
    </div>
  );
};

export default RestaurantDashboard;
